import React from 'react'

import { Field } from 'formik'

import { ZariContext } from '_zari/context'

import { Form, Col } from '_zari/core'

import * as UsuarioCrud from 'services/usuario.service'

const UsuarioSolicitanteFormGroup = ({ vIdOficinaSolicitante, vIdUsuarioSolicitante, iError, iTouched, fIsSubmitting, disabled }) => {
  const __zariContext = React.useContext(ZariContext)
  const [usuarios, setUsuarios] = React.useState([])
  const [usuarioExterno, setUsuarioExterno] = React.useState(null)

  // Normalizar: el valor puede ser string (una oficina) o array (OC con múltiples oficinas
  // provenientes de requerimientos). En array se consulta por cada oficina y se concatena.
  const oficinasArr = React.useMemo(() => {
    if (!vIdOficinaSolicitante) return []
    if (Array.isArray(vIdOficinaSolicitante)) return vIdOficinaSolicitante.filter(Boolean)
    return [vIdOficinaSolicitante]
  }, [vIdOficinaSolicitante])

  const oficinasKey = oficinasArr.join('|')

  React.useEffect(() => {
    if (oficinasArr.length === 0) {
      setUsuarios([])
      return
    }
    let cancelled = false
    Promise.all(oficinasArr.map(id => UsuarioCrud.obtenerTodoAsSelect(id).then(r => r.data || []).catch(() => [])))
      .then(resultados => {
        if (cancelled) return
        const mapa = new Map()
        for (const lista of resultados) {
          for (const u of lista) {
            if (u && u._id) mapa.set(u._id, u)
          }
        }
        setUsuarios([...mapa.values()])
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oficinasKey])

  // Si el usuario asignado (viene del requerimiento) no está en la lista de la oficina,
  // hacemos un fetch puntual para mostrar su nombre real en lugar de un placeholder.
  React.useEffect(() => {
    if (!vIdUsuarioSolicitante) {
      setUsuarioExterno(null)
      return
    }
    const enLista = usuarios.some(u => u._id === vIdUsuarioSolicitante)
    if (enLista) {
      setUsuarioExterno(null)
      return
    }
    let cancelled = false
    UsuarioCrud.obtenerxIdAsEdicion(vIdUsuarioSolicitante)
      .then(({ data }) => {
        if (!cancelled && data) setUsuarioExterno(data)
      })
      .catch(() => {
        if (!cancelled) setUsuarioExterno(null)
      })
    return () => {
      cancelled = true
    }
  }, [vIdUsuarioSolicitante, usuarios])

  return (
    <Form.Group>
      <Form.Label text='USUARIO SOLICITANTE' htmlFor='select-usuariosolicitante' md={4} alignRightAt='md' />
      <Col md={8}>
        <Field name='_idUsuarioSolicitante'>
          {({ field }) => {
            const valorActual = field.value || ''
            const existeEnLista = usuarios.some(u => u._id === valorActual)
            const nombreExterno = usuarioExterno && usuarioExterno._id === valorActual
              ? `${(usuarioExterno.nombres || '').toUpperCase()} ${(usuarioExterno.apellidos || '').toUpperCase()}`.trim() ||
                usuarioExterno.username ||
                '(USUARIO DEL REQUERIMIENTO)'
              : '(USUARIO DEL REQUERIMIENTO)'
            return (
              <Form.Select
                {...field}
                disabled={fIsSubmitting || disabled || oficinasArr.length === 0}
                isInvalid={iTouched && iError}
                id='select-usuariosolicitante'
              >
                <option value=''>Seleccionar</option>
                {/* Si el usuario solicitante viene de un requerimiento y no está
                    entre los usuarios de la oficina, mostramos su nombre real
                    obteniéndolo por id (con fallback al placeholder genérico). */}
                {valorActual && !existeEnLista && (
                  <option value={valorActual}>{nombreExterno}</option>
                )}
                {usuarios.map((val, key) => (
                  <option key={key} value={val._id}>
                    {val.nombres?.toUpperCase()} {val.apellidos?.toUpperCase()}
                  </option>
                ))}
              </Form.Select>
            )
          }}
        </Field>
        {iTouched && iError ? <Form.Feedback type='invalid'>{iError}</Form.Feedback> : null}
      </Col>
    </Form.Group>
  )
}

export default React.memo(UsuarioSolicitanteFormGroup)
